import { useState, useEffect } from 'react';
import { createApiClient } from '../../../services/ApiClient.jsx';

export const useTopicData = (accessToken, courseId, topicId) => {
    const [course, setCourse] = useState(null);
    const [topic, setTopic] = useState(null);
    const [formData, setFormData] = useState(null);
    const [allQuestions, setAllQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const apiClient = createApiClient(accessToken);

    const fetchData = async () => {
        try {
            setLoading(true);
            console.log('🔍 Starting fetchData with:', { courseId, topicId, accessToken: !!accessToken });
            
            // Fetch course data
            console.log('📚 Fetching course data...');
            const courseResponse = await apiClient.get(`/api/courses/${courseId}`);
            console.log('✅ Course response:', courseResponse);
            setCourse(courseResponse);

            // Fetch topic data
            console.log('📋 Fetching topics data...');
            const topicsResponse = await apiClient.get(`/api/courses/${courseId}/topics`);
            console.log('✅ Topics response:', topicsResponse);
            const foundTopic = topicsResponse.topics.find(t => t.id === topicId);
            if (!foundTopic) {
                throw new Error('Topic not found');
            }
            console.log('✅ Found topic:', foundTopic);
            setTopic(foundTopic);

            // Fetch form data
            console.log('📝 Fetching form data...');
            const formResponse = await apiClient.get(`/api/courses/${courseId}/topics/${topicId}/form`);
            console.log('✅ Form response:', formResponse);
            setFormData(formResponse);

            // Process questions - flatten all questions from all sections
            const questions = [];
            if (formResponse?.sections) {
                formResponse.sections.forEach((section) => {
                    section.questions?.forEach((question) => {
                        questions.push({
                            ...question,
                            sectionId: section.id,
                            sectionTitle: section.title,
                        });
                    });
                });
            }
            
            console.log('✅ Processed questions:', questions);
            
            // Add the flattened questions array to formData for compatibility
            const enrichedFormData = {
                ...formResponse,
                questions: questions
            };
            
            setFormData(enrichedFormData);
            setAllQuestions(questions);
            setError(null);
        } catch (err) {
            console.error('❌ Error fetching topic data:', err);
            
            // If we get a 403 error, try to join the course first
            if (err.response?.status === 403) {
                try {
                    console.log('Access denied for topic, attempting to join course automatically...');
                    await apiClient.post(`/api/courses/${courseId}/join`);
                    
                    // After joining, try to fetch the individual components again
                    console.log('🔄 Retrying data fetch after joining course...');
                    
                    // Retry course data
                    const courseResponse = await apiClient.get(`/api/courses/${courseId}`);
                    setCourse(courseResponse);

                    // Retry topic data
                    const topicsResponse = await apiClient.get(`/api/courses/${courseId}/topics`);
                    const foundTopic = topicsResponse.topics.find(t => t.id === topicId);
                    if (!foundTopic) {
                        throw new Error('Topic not found');
                    }
                    setTopic(foundTopic);

                    // Retry form data
                    const formResponse = await apiClient.get(`/api/courses/${courseId}/topics/${topicId}/form`);
                    
                    // Process questions
                    const questions = [];
                    if (formResponse?.sections) {
                        formResponse.sections.forEach((section) => {
                            section.questions?.forEach((question) => {
                                questions.push({
                                    ...question,
                                    sectionId: section.id,
                                    sectionTitle: section.title,
                                });
                            });
                        });
                    }
                    
                    const enrichedFormData = {
                        ...formResponse,
                        questions: questions
                    };
                    
                    setFormData(enrichedFormData);
                    setAllQuestions(questions);
                    setError(null);
                    setLoading(false);
                    return;
                } catch (joinErr) {
                    console.error('Error auto-joining course for topic:', joinErr);
                    // If joining fails, show a more helpful error message
                    if (joinErr.response?.status === 409) {
                        setError('You are already enrolled in this course but still cannot access this topic. Please contact your instructor.');
                    } else {
                        setError('Unable to access topic. Please try joining the course first.');
                    }
                    setLoading(false);
                    return;
                }
            }
            
            setError('Failed to load topic. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        console.log('🚀 useTopicData useEffect triggered:', { accessToken: !!accessToken, courseId, topicId });
        if (accessToken && courseId && topicId) {
            console.log('✅ All parameters present, calling fetchData');
            fetchData();
        } else {
            console.log('❌ Missing parameters:', { accessToken: !!accessToken, courseId, topicId });
        }
    }, [accessToken, courseId, topicId]);

    return {
        course,
        topic,
        formData,
        allQuestions,
        loading,
        error
    };
};
